<script setup lang="ts">
import { useFuse } from '@vueuse/integrations/useFuse'
import type { Product } from '~/types/products'
import type { LlanaListResponse, LlanaWhere } from '~/types/llana'
import type { ForumPost } from '~/types/forum'
import { useProductsStore } from '~/stores/product/Products'
import { usePostsStore } from '~/stores/forum/Posts'
import { useMagazineArticlesStore } from '~/stores/magazine/Articles'
import { useUserStore } from '~/stores/user/Users'
import ProductPreview from '~/components/Product/ProductPreview.vue'
import UserPreview from '~/components/Profile/UserPreview.vue'
import Post from '~/components/Discussions/Post.vue'
import type { MagazineArticle } from '~/types/magazine'
import ArticlePreview from '~/components/Magazine/ArticlePreview.vue'
import type { Profile } from '~/types/profile'

const props = defineProps<{
	domains?: string[] // products, posts, articles, users
	boxClass?: string
	placeholder?: string
	hideCreate?: boolean
	emitResult?: boolean // If true, emit the selected result, dont click to navigate
	disabled?: boolean
	closeOnSelect?: boolean
	showTypeTag?: boolean
	label?: string
	labelPosition?: "stacked" | "floating" | "fixed" | undefined
}>()

const emits = defineEmits(['selected', 'close'])

const searchQuery = ref('')
let searchResults = ([] as Product[] | ForumPost[] | MagazineArticle[] | Profile[])
const loading = ref<boolean>(true)

const placeholder = computed(() => props.placeholder || `Search ${props.domains?.join(', ')}...`)

const products = ref<LlanaListResponse<Product> | undefined>(undefined)
const posts = ref<LlanaListResponse<ForumPost> | undefined>(undefined)
const articles = ref<LlanaListResponse<MagazineArticle> | undefined>(undefined)
const users = ref<LlanaListResponse<Profile> | undefined>(undefined)

const showProducts = computed<boolean>(() => (props.domains?.includes('products') ? true : false))
const showPosts = computed<boolean>(() => (props.domains?.includes('posts') ? true : false))
const showArticles = computed<boolean>(() => (props.domains?.includes('articles') ? true : false))
const showUsers = computed<boolean>(() => (props.domains?.includes('users') ? true : false))


async function fetchProducts(){
	if (showProducts.value) {
		const productStore = useProductsStore()

		try{
			products.value = await productStore().search({
				limit: 99999, // Load all products
				fields: [
					'id',
					'name',
					'handle',
					'review_rating',
					'image_url',
					'category_id',
					'product_categories.id',
					'product_categories.handle',
				],
				relations: 'product_categories', // Use full table name for relation
				where: <LlanaWhere[]>[],
			})
		}catch(e){
			console.error(e)
		}
	}
}

async function fetchDiscussions(){
	if (showPosts.value) {
		const postStore = usePostsStore(0)

		try{
			posts.value = await postStore().search({
				limit: 99999, // Load all posts
				fields: ['id', 'title', 'handle', 'forum_id', 'user_id', 'reply_count', 'votes', 'created_at'],
				relations: 'users,forums,forums_posts_upvotes,forums_posts_downvotes',
				where: <LlanaWhere[]>[],
			})
		}catch(e){
			console.error(e)
		}
	}
}

async function fetchArticles(){

	if (showArticles.value) {
		const articleStore = useMagazineArticlesStore(0)

		try{
			articles.value = await articleStore().search({
				limit: 99999, // Load all articles
				fields: ['id', 'title', 'handle', 'image_url', 'category_id', 'comment_count', 'created_at'],
				relations: 'magazine_categories',
				where: <LlanaWhere[]>[],
			})

		}catch(e){
				console.error(e)
			}
		}
}

async function fetchUsers(){
	if (showUsers.value) {
		const userStore = useUserStore()

		try{
			users.value = await userStore().search({
				limit: 999999, // Load all users
				fields: ['id', 'username', 'avatar_url', 'active_at'],
				relations: '',
				sort: 'active_at.desc',
				where: <LlanaWhere[]>[],
			})
		}catch(e){
			console.error(e)
		}
	}
}



async function fetchAll() {
	await Promise.all([fetchDiscussions(), fetchArticles(), fetchProducts(), fetchUsers()])
	runSearchFuse()
	loading.value = false
}

function runSearchFuse(){

	let { results: postFuseResults } = useFuse(
			searchQuery,
			(posts.value as LlanaListResponse<ForumPost>)?.data,
			computed(() => ({
				fuseOptions: {
					threshold: 0.2,
					includeScore: true,
					keys: ['title']
				}
			}))
		)

	let { results: articlesFuseResults } = useFuse(
				searchQuery,
				(articles.value as LlanaListResponse<MagazineArticle>)?.data,
				computed(() => ({
					fuseOptions: {
						threshold: 0.2,
						includeScore: true,
						keys: ['title']
					}
				}))
			)

			let { results: productsFuseResults } = useFuse(
				searchQuery,
				(products.value as LlanaListResponse<Product>)?.data,
				computed(() => ({
					fuseOptions: {
						threshold: 0.2,
						includeScore: true,
						keys: ['name']
					}
				}))
			)

			let { results: usersFuseResults } = useFuse(
				searchQuery,
				(users.value as LlanaListResponse<Profile>)?.data,
				computed(() => ({
					fuseOptions: {
						threshold: 0.2,
						includeScore: true,
						keys: ['username']
					}
				}))
			)



	searchResults = computed(() => {
		let results = [
			...showProducts.value && products.value?.data?.length
				? productsFuseResults?.value?.map((result: any) => { return { ...products.value?.data[result.refIndex], searchType: 'product', searchScore: result.score } })
				: [] as Product[],
			...showPosts.value && posts.value?.data?.length
			 	? postFuseResults?.value?.map((result: any) => { return { ...posts.value?.data[result.refIndex], searchType: 'post', searchScore: result.score } })
				: [] as ForumPost[],
			...showArticles.value && articles.value?.data?.length
				? articlesFuseResults?.value?.map((result: any) => { return { ...articles.value?.data[result.refIndex], searchType: 'article', searchScore: result.score } })
				: [] as MagazineArticle[],
			...showUsers.value && users.value?.data?.length
				? usersFuseResults?.value?.map((result: any) => { return { ...users.value?.data[result.refIndex], searchType: 'user', searchScore: result.score } })
				: [] as Profile[],
		]

		//sort results by searchScore lowest to highest
		results.sort((a:any, b:any) => a.searchScore - b.searchScore)

		//return top 5 results
		results = results.slice(0, 5)

		return results
	})

}

function clicked(result: any){
	if(props.emitResult){
		searchQuery.value = ''
		emits('selected', result)
	}
}

watch(searchQuery, async () => {
	runSearchFuse()
})

await fetchAll()
</script>

<template>

	<label v-if="label" :position="labelPosition ?? 'stacked'" class="form-label">{{ label }}</label>

	<div class="flex flex-col items-center">

		 <!-- Loading indicator -->
		<div v-if="loading" class="text-center">
				<div class="animate-spin rounded-full h-8 w-8 border-2 border-green-600 border-t-transparent justify-self-center" />
				<p class="text-green-600 text-xs pt-2">Loading Search...</p>
		</div>

			<div v-else class="flex w-full" :class="props.boxClass">

				<!-- Search Input -->
				<input
					v-model="searchQuery"
					:disabled="props.disabled"
					type="text"
					:placeholder="placeholder"
					class="w-full px-4 py-2 border text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
				/>
			</div>

			<!-- Search Results -->
			<div
				v-if="
				searchQuery.length > 0 && searchResults.length"
				class="relative z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
				<ProductPreview
					v-for="product in searchResults.filter((r: any) => r.searchType === 'product')"
					:key="product?.id"
					:product="product as Product"
					theme="search"
					closeOnSelect
					:emitResult="emitResult"
					:showTypeTag="showTypeTag"
					@selected="clicked(product)"
					@close="emits('close')"
					/>

				<Post
					v-for="post in searchResults.filter((r: any) => r.searchType === 'post')"
					:key="post.id"
					:forum="(post as ForumPost).forums[0]"
					:post="post as ForumPost"
					destination="post"
					theme="search"
					closeOnSelect
					:emitResult="emitResult"
					:showTypeTag="showTypeTag"
					@selected="clicked(post)"
					@close="emits('close')"
				/>

				<ArticlePreview
					v-for="article in searchResults.filter((r: any) => r.searchType === 'article')"
					:key="article.id"
					:article="article as MagazineArticle"
					hide-category
					closeOnSelect
					theme="search"
					:showTypeTag="showTypeTag"
					:emitResult="emitResult"
					@selected="clicked(article)"
					@close="emits('close')"
				/>

				<UserPreview
					v-for="user in searchResults.filter((r: any) => r.searchType === 'user')"
					:key="user?.id"
					:user="user as Profile"
					theme="search"
					closeOnSelect
					:emitResult="emitResult"
					:showTypeTag="showTypeTag"
					@selected="clicked(user)"
					@close="emits('close')"
					/>

			</div>

			<div
				v-else-if="searchQuery.length > 0 && !hideCreate"
				class="relative z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col p-3">
				<h3 class="text-green-500">Cannot find what you're looking for?</h3>

				<div v-if="showPosts" class="mb-2">
					<Icon name="ph:chats-circle-duotone" class="h-5 w-5 text-green-500 icon-fix-mb3 mr-1" />
					<RouterLink to="/discussions/nutrition-wellness/new"
						><span class="text-md mb-2">Start a discussion</span></RouterLink
					>
				</div>

				<div v-if="showArticles" class="mb-2">
					<Icon name="ph:newspaper-clipping-duotone" class="h-5 w-5 text-green-500 icon-fix-mb3 mr-1" />
					<RouterLink to="/magazine/request"
						><span class="text-md mb-2">Request a magazine article</span></RouterLink
					>
				</div>

				<div v-if="showProducts" class="mb-2">
					<Icon name="ph:shopping-bag-duotone" class="h-5 w-5 text-green-500 icon-fix-mb3 mr-1" />
					<RouterLink to="/products/new"><span class="text-md mb-2">Suggest a product listing</span></RouterLink>
				</div>
			</div>

	</div>
</template>
